/**
 * The opening brief: what a controller reads off the ATIS and the strip board before taking the
 * frequency — the runways and approach in use, the wind, and the procedures the traffic will be
 * flying. `atisBriefing` is pure so it can be tested without a DOM; `createAtisPanel` renders it
 * into the game's existing briefing panel.
 */
import { proceduresByRunway, toMagneticDeg, type AirportData, type RunwayConfig } from '@atc/core';
import { runwayChip } from './overlays';

/** Assumption: more procedure names than this on one line stop being readable at a glance. */
const MAX_NAMES = 6;

export interface AtisBriefing {
  /** "LLBG · BEN GURION". */
  title: string;
  /** The approach frequency's callsign, the station the arrivals will call. */
  station: string;
  /** "ARR 12 ILS · DEP 26", the same chip the header shows. */
  runways: string;
  /** True when the tailwind is over the published limit; the panel flags it. */
  runwaysOver: boolean;
  /** "290° at 8 kt", or "calm". */
  wind: string;
  /** "19:15 · Rain", with the METAR-style line under it; absent when no conditions were given. */
  weather?: string;
  metar?: string;
  /** Head or tailwind on each runway in use, in words. */
  windDetail: string;
  elevation: string;
  /** STAR names feeding the landing runway, capped, with a "+n more" tail. */
  arrivalsVia: string[];
  /** SID names off the departure runway, capped the same way. */
  departuresVia: string[];
  /** "Climb departures to 5,000 ft initially." and the other standing instructions. */
  notes: string[];
}

const names = (procedures: { name: string }[]): string[] => {
  const sorted = procedures.map((p) => p.name).sort();
  return sorted.length > MAX_NAMES ? [...sorted.slice(0, MAX_NAMES), `+${sorted.length - MAX_NAMES} more`] : sorted;
};

const ft = (n: number) => `${Math.round(n).toLocaleString('en-US')} ft`;

export interface AtisOptions {
  tower?: boolean;
  /** How many aircraft are already on frequency; omitted outside a live session. */
  traffic?: number;
  /** Display name, which lives in `airports.ts` — the openScope files only carry radio callsigns. */
  name?: string;
  /** The session's wind, when the chosen weather differs from the airport file's. */
  wind?: { directionDeg: number; speedKt: number };
  /** The session's time and weather, already worded, and its METAR-style line. */
  conditions?: { weather: string; metar: string };
}

export function atisBriefing(airport: AirportData, config: RunwayConfig, options: AtisOptions = {}): AtisBriefing {
  const chip = runwayChip(config);
  const groups = new Map(proceduresByRunway(airport).map((g) => [g.runway, g]));
  const w = options.wind ?? airport.wind;
  // The header chip already works out head/tailwind for its tooltip; show it instead of hiding it.
  const windDetail = chip.title.slice(0, chip.title.indexOf(' Click for the briefing.'));
  const notes = [
    `Assigning an initial altitude before take-off gives a continuous climb; ${ft(airport.initialAltitudeFt)} is standard. Without one, the pilot takes off, climbs 1,000 ft above the field and asks for instructions.`,
    `Descend an arrival to about 3,000 ft and turn it onto a 30° intercept before you clear it for runway ${config.arrival.runways.join(' or ')}: a clearance is always accepted, but an aircraft too high to reach the glideslope goes around.`,
    'Click a blip or a strip to see what that pilot is waiting for and what to say.',
  ];
  if (options.tower) notes.splice(0, notes.length,
    'You control the runway. Accept transfers from approach and ground in the Tower position panel.',
    `Departures already have their initial altitude of ${ft(airport.initialAltitudeFt)}. Use line up and wait or takeoff when the runway is available.`,
    'Approach delivers established arrivals without landing clearance. Request wider spacing or suspend new arrivals on the coordination channel.',
    'After a go-around: runway heading, climb to the higher of present altitude or field elevation plus 3,000 ft rounded up to a thousand. Transfer to approach for resequencing. This is a simplified game procedure.',
    'Traffic comes in 15-minute banks: a quiet opening, a busier middle, then a lighter handover period. Coordinate upstream spacing when you need room.',
    'Transfer departures when offered, and arrivals to ground after they vacate. Estimated arrival times are advisory, not a clearance to use the runway.');
  if (options.traffic) notes.unshift(`${options.traffic} aircraft on frequency; they will check in one at a time.`);
  return {
    title: [airport.icao, (options.name ?? airport.name)?.toUpperCase()].filter(Boolean).join(' · '),
    station: options.tower ? airport.radio.twr : airport.radio.app,
    runways: chip.text,
    runwaysOver: chip.over,
    // ATIS gives the wind magnetic, like the runway it favours (a METAR stays true).
    wind: w.speedKt < 1 ? 'calm' : `${String(Math.round(toMagneticDeg(airport, w.directionDeg)) || 360).padStart(3, '0')}° at ${Math.round(w.speedKt)} kt`,
    windDetail,
    ...(options.conditions ? { weather: options.conditions.weather, metar: options.conditions.metar } : {}),
    elevation: ft(airport.elevationFt),
    arrivalsVia: names(config.arrival.runways.flatMap((r) => groups.get(r)?.stars ?? [])),
    departuresVia: names(config.departure.runways.flatMap((r) => groups.get(r)?.sids ?? [])),
    notes,
  };
}

export interface AtisPanelOptions {
  onStart(): void;
  onBriefing(): void;
  onSuppress(suppress: boolean): void;
}

const esc = (s: string) => s.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]!);
const row = (label: string, value: string) => `<div class="atis-row"><span>${esc(label)}</span><b>${esc(value)}</b></div>`;
const chips = (label: string, list: string[]) =>
  list.length ? `<div class="atis-row"><span>${esc(label)}</span><span class="atis-chips">${list.map((n) => `<code>${esc(n)}</code>`).join('')}</span></div>` : '';

/** Renders into the game's `.g-challenge` panel; the caller keeps the sim paused until `onStart`. */
export function createAtisPanel(panel: HTMLElement, brief: AtisBriefing, options: AtisPanelOptions): void {
  panel.classList.add('atis');
  panel.hidden = false;
  panel.innerHTML = `<h2>${esc(brief.title)}</h2>
    <p class="atis-station">${esc(brief.station)} — you have the frequency.</p>
    <div class="atis-plate${brief.runwaysOver ? ' over' : ''}">
      ${row('In use', brief.runways)}
      ${row('Wind', brief.wind)}
      ${brief.weather ? row('Weather', brief.weather) : ''}
      ${row('Elevation', brief.elevation)}
      ${chips('Arrivals via', brief.arrivalsVia)}
      ${chips('Departures via', brief.departuresVia)}
    </div>
    ${brief.metar ? `<p class="atis-wind"><code>${esc(brief.metar)}</code></p>` : ''}
    <p class="atis-wind">${esc(brief.windDetail)}</p>
    <ul class="atis-notes">${brief.notes.map((n) => `<li>${esc(n)}</li>`).join('')}</ul>
    <div class="atis-actions">
      <button type="button" class="g-btn atis-start">Take the frequency</button>
      <button type="button" class="g-btn atis-brief">Full briefing</button>
    </div>
    <label class="atis-skip"><input type="checkbox"> Don't show this again</label>`;
  panel.querySelector('.atis-start')!.addEventListener('click', () => { panel.hidden = true; options.onStart(); });
  panel.querySelector('.atis-brief')!.addEventListener('click', () => options.onBriefing());
  panel.querySelector<HTMLInputElement>('.atis-skip input')!.addEventListener('change', (e) => options.onSuppress((e.target as HTMLInputElement).checked));
}
